
import React, { useState } from 'react';
import { SessionForm } from './SessionForm';
import { useNavigate } from 'react-router-dom';
import { EditSession } from './EditSession';
import { DeleteConfirmationPopup, DeletePopup } from './DeletePopup';
import { PostSessions } from '../../service/SessionAPI';
import { Toast } from './Toast';

export const ManageSession = ({ sessionData, fetchSessions }) => {
    const [selectedSession, setSelectedSession] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isManagingSlots, setIsManagingSlots] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [slotToDelete, setSlotToDelete] = useState(null);
    const navigate = useNavigate();

    const handleEditClick = (session) => {
        setSelectedSession(session);
        setIsEditing(true);
    };

    const handleManageSlotsClick = (session, slot) => {
        setSelectedSession(session);
        setSelectedSlot(slot);
        setIsManagingSlots(true);
    };

    const handleClose = () => {
        setIsCreating(false);
        setIsEditing(false);
        setIsManagingSlots(false);
        setIsDeletePopupOpen(false);
        setSelectedSession(null);
    };

    const formatSessionDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day)); // treat as UTC date, avoid timezone offset
        const options = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString('en-GB', options);
    };

    const handleDateClick = (sessionId, slotId) => {
        const session = sessionData.find(s => s.session_id === sessionId);
        const slot = session?.slots.find(s => s.slot_id === slotId);
        navigate(`/participants/${sessionId}/${slotId}`, {
            state: {
                seatAvailable: slot?.seat_available || 0,
                registeredCount: slot?.registered_count || 0,
            }
        });
    };

    const handleDeleteClick = (session, slot) => {
        setSlotToDelete({ session, slot });
        setIsDeletePopupOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!slotToDelete) return;

        const { session, slot } = slotToDelete;
        const payload = {
            session_id: session.session_id,
            quarter: session.quarter,
            session_name: session.session_name,
            session_count: session.session_count,
            session_details: [
                {
                    slot_id: slot.slot_id,
                    session_date: slot.session_date,
                    start_time: slot.start_time,
                    end_time: slot.end_time,
                    seat_available: slot.seat_available,
                    status: "Delete"
                }
            ],
            empId: session.empId
        };

        try {
            const response = await PostSessions(payload);
            setIsDeletePopupOpen(false);
            setSlotToDelete(null);
            if (response.data.code === 200) {
                Toast.success("Session cancelled successfully");
                fetchSessions();
                // Optionally refresh session data here
            } else {
                Toast.error("Failed to cancel the session");
                fetchSessions();
            }
        } catch (error) {
            setIsDeletePopupOpen(false);
            setSlotToDelete(null);
            Toast.error("Failed to cancel the session");
        }
    };
    return (
        <div className="row pb-3 flex-nowrap overflow-auto">
            {sessionData.length > 0 ? (
                sessionData.map((session) => (
                    <div className="col-lg-3 col-md-6" key={session.session_id}>
                        <div className="kanban-container bg-white rounded-3 h-100 p-3" style={{ backgroundColor: 'lightblue' }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <p className="font-16 semibold-text mb-1">{session.session_name}</p>
                                    <p className="font-14 medium-text grey-v2 mb-0">Quarter {session.quarter}</p>
                                </div>
                                <button className="bg-transparent border-0 pe-0" onClick={() => handleEditClick(session)}>
                                    <img src="/Images/edit-action.svg" alt="edit" />
                                </button>
                            </div>
                            <div className="kanban-body d-flex flex-column gap-3 mt-4 pe-1 overflow-auto">
                                {session.slots.map((slot) => {
                                    const slotEndTime = new Date(slot.end_time);
                                    const isCompleted = new Date(slot.session_date) < new Date() && slotEndTime < new Date();
                                    const isRegistered = slot.status === 'Register';
                                    const isRescheduled = slot.status === 'Reschedule';
                                    const isRemoved = slot.status === 'Remove';
                                    const isPast = slot.days_left === 'Past';
                                    const isActive = slot.is_active === false;

                                    // Determine if the duration indicator should be hidden
                                    const hideDurationIndicator = isCompleted || isRegistered || isRescheduled || isRemoved || isPast || isActive;

                                    // Calculate percentage of registrations
                                    const registeredCount = slot.registered_count || 0;
                                    const seatAvailable = slot.seat_available || 1; // Avoid division by zero
                                    const percentage = (registeredCount / seatAvailable) * 100;
                                    // Determine progress bar class based on percentage
                                    let progressClass = 'low'; // Default to low
                                    if (percentage > 80) {
                                        progressClass = 'high';
                                    } else if (percentage > 50) {
                                        progressClass = 'medium';
                                    }

                                    return (
                                        <div className={`details-card p-3 rounded-3 position-relative`} key={slot.slot_id}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="font-14 bold-text session-date mb-0" style={{ cursor: 'pointer' }} onClick={() => handleDateClick(session.session_id, slot.slot_id)}>{formatSessionDate(slot.session_date)}</p>
                                                    <span className="font-11 medium-text">{`${slot.start_time} - ${slot.end_time}`}</span>
                                                </div>
                                                <div className={`duration-indicator d-flex align-items-center justify-content-center gap-3 ${hideDurationIndicator ? 'd-none' : ''}`}>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <img src="./Images/timer-icon.svg" alt="timer-icon" />
                                                        <span className="font-10 regular-text grey-text">{slot.days_left}</span>
                                                    </div>

                                                    <div className="dropdown">
                                                        <button
                                                            className="bg-transparent border-0 p-0 d-flex align-items-center"
                                                            type="button"
                                                            id={`dropdownMenuButton-${slot.slot_id}`}
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="true"
                                                        >
                                                            <img src="Images/menu-options.svg" alt="menu" />
                                                        </button>

                                                        <div className="dropdown-menu mt-2 py-0 card-menu border-0" aria-labelledby={`dropdownMenuButton-${slot.slot_id}`}>
                                                            <ul className="list-group mx-2">
                                                                <li className="list-group-item p-0 border-0 border-bottom" aria-current="true">
                                                                    <button type="button" className="btn font-11 medium-text w-100 d-flex align-items-center justify-content-start px-1 py-2 border-0" onClick={() => handleManageSlotsClick(session, slot)}>Edit</button>
                                                                </li>
                                                                <li className="list-group-item p-0 border-0" aria-current="true">
                                                                    <button type="button" className="btn font-11 medium-text w-100 d-flex align-items-center justify-content-start px-1 py-2 border-0" onClick={() => handleDeleteClick(session, slot)}>Cancel</button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Show status based on conditions */}
                                            {isActive && (
                                                <span className="font-10 medium-text status-label-cancel visible">Cancelled</span>
                                            )}
                                            {isCompleted && (
                                                <span className="font-10 medium-text status-label-success visible">Completed</span>
                                            )}
                                            {isRegistered && (
                                                <span className="font-10 medium-text status-label-success visible">Registered</span>
                                            )}
                                            {isRescheduled && (
                                                <span className="font-10 medium-text status-label-success visible">Registered</span>
                                            )}
                                            {isRemoved && (
                                                <span className="font-10 medium-text status-label-cancel visible">Cancelled</span>
                                            )}
                                            {isPast && !isCompleted && (
                                                <span className="font-10 medium-text status-label-success visible">Completed</span>
                                            )}

                                            <div className="registrations-progress-bar d-flex flex-column mt-4">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span className="font-10 regular-text grey-text">Registrations</span>
                                                    <span className="font-10 regular-text grey-text">{registeredCount}/{seatAvailable}</span>
                                                </div>

                                                <div className="progress">
                                                    <div
                                                        className={`progress-bar ${progressClass}`} // Apply the correct class
                                                        role="progressbar"
                                                        style={{ width: `${percentage}%` }} // Set width dynamically
                                                        aria-valuenow={percentage}
                                                        aria-valuemin={0}
                                                        aria-valuemax={100}
                                                    ></div>

                                                </div>

                                                {/* Display status label based on slot status */}
                                                {slot.status && (
                                                    <span className={`font-10 medium-text status-label-${slot.status === 'Remove' ? 'cancel' : 'success'}`}>
                                                        {slot.status === 'Remove' ? 'Cancelled' : slot.status}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))
            ) : (
                <div className="row">
                    <div className="d-flex flex-column align-items-center justify-content-center container-height">
                        <img className="empty-img-width mb-4" src="Images/table-empty-state.svg" alt="empty-state" />
                        <p className="font-20 medium-text grey-text mb-1">Currently, No sessions available to display</p>
                        <p className="font-12 medium-text grey-v2">Click below to create new session</p>

                        <button className="btn hive-btn-primary text-nowrap" onClick={() => setIsCreating(true)}>
                            <img src="Images/hive-add-icon.svg" className="me-2" alt="add-icon" />Create Session
                        </button>
                    </div>
                </div>
            )}

            {/* Render SessionForm for editing */}
            {isEditing && selectedSession && (
                <SessionForm
                    onClose={handleClose}
                    empId={selectedSession.empId}
                    sessionData={selectedSession}
                    slots={selectedSession.slots}
                    fetchSessions={fetchSessions}
                />
            )}

            {/* Render SessionForm for managing slots */}
            {isManagingSlots && selectedSession && selectedSlot && (
                <EditSession
                    onClose={handleClose}
                    empId={selectedSession.empId}
                    sessionData={selectedSession}
                    slot={selectedSlot}
                    fetchSessions={fetchSessions}
                />
            )}

            {/* Render SessionForm for creating a new session */}
            {isCreating && (
                <SessionForm
                    onClose={handleClose}
                    fetchSessions={fetchSessions}
                />
            )}
            <DeleteConfirmationPopup
                show={isDeletePopupOpen}
                onClose={() => {
                    setIsDeletePopupOpen(false);
                    setSlotToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                sessionDate={
                    slotToDelete ? formatSessionDate(slotToDelete.slot.session_date) : ""
                }
            />

        </div>
    );
};
